































































import { computed, defineComponent, onBeforeUnmount, ref, set, watch } from "@vue/composition-api";
import {
  InputUserAbuseReport,
  ReportReason,
  UserAbuseReport,
  UserAbuseReportType,
} from "@scrile/api-provider/dist/api/ReportsProvider";
import { Subject } from "@scrile/api-provider/dist/api/SubjectProvider";
import useAuthUser from "@/hooks/useAuthUser";
import useFiles, { UploadFilesData } from "@/hooks/useFiles";
import providers from "@/lib/api";
import emitter from "@/lib/emitter";
import { calculateProgress } from "@/lib/FileUploader";
import { $t } from "@/lib/i18nHelpers";
import { AbuseReportData, FormFileUpload, FormValues, NotificationData, QuickTip } from "@/types";
import { EVENTS } from "@/const";
import BaseIcon from "@/components/Base/BaseIcon";
import BaseButton from "@/components/BaseButton";
import BaseSpinner from "@/components/BaseSpinner";
import BaseStepper from "@/components/BaseStepper";
import ModalView from "@/components/ModalView";
import useStepperData from "@/components/PostCreate/hooks/useStepperData";
import QuickTipsItem from "@/components/QuickTipsItem";
import ReportProofsForm from "./components/ReportProofsForm.vue";
import ReportReasonsForm from "./components/ReportReasonsForm.vue";

export default defineComponent({
  name: "ReportContentModal",
  components: {
    ReportProofsForm,
    ReportReasonsForm,
    QuickTipsItem,
    BaseIcon,
    BaseButton,
    BaseStepper,
    BaseSpinner,
    ModalView,
  },

  setup() {
    const { authUser } = useAuthUser();
    const { getUploadUrl, uploadFilesToS3, updateFile } = useFiles();
    const loading = ref(false);
    const processing = ref(false);
    const isValid = ref(true);

    const reportSubject = ref<Subject<UserAbuseReportType> | null>(null);
    const reportedUserId = ref<string | null>(null);
    const reasons = ref<ReportReason[]>([]);
    const selectedReasonIds = ref<string[]>([]);

    const proofFilesList = ref<FormFileUpload[]>([]);

    const guestInfoValues = ref<FormValues>({});
    const comment = ref("");

    const show = computed(() => !!reportSubject.value && !!reportedUserId.value);
    const selectedReasons = computed(() => reasons.value.filter(i => selectedReasonIds.value.includes(i.id)));

    const stepsArray = computed(() => ["reason", "additionalInfo"]);
    const { stepperElRef, currentStep, steps, getStepName, onCurrentStepChange } = useStepperData(stepsArray);

    const tipItem = computed<QuickTip>(() => ({
      icon: "msr-info",
      color: "secondary",
      text: $t(
        `We are doing our best so you will enjoy the content provided. If, in any case, you have a complaint, please let us know. {br}{br} All complaints will be reviewed and resolved within seven business days.`
      ),
    }));

    const onAddFile = (fileList: FileList) => {
      const fileData = Array.from(fileList).map(i => ({
        file: i,
        uploadProgress: 100,
      }));
      proofFilesList.value = [...proofFilesList.value, ...fileData];
    };

    const onRemoveFile = (index: number) => {
      proofFilesList.value.splice(index, 1);
    };

    const getReportReasons = async () => {
      try {
        loading.value = true;
        reasons.value = await providers.ReportsProvider.search({ data: {} });
      } finally {
        loading.value = false;
      }
    };

    const onCloseModal = () => {
      reportSubject.value = null;
      reportedUserId.value = null;
      selectedReasonIds.value = [];
      comment.value = "";
      proofFilesList.value = [];
      guestInfoValues.value = {};
    };

    const uploadFiles = async (report: UserAbuseReport) => {
      const promises: Promise<void>[] = [];

      const fileIds = proofFilesList.value.map((item, index) => ({
        subjectId: report.id,
        type: "abuse_report",
        fileNumber: index,
      }));

      const uploadUrls = await getUploadUrl(fileIds);

      const uploadData: UploadFilesData[] = [];
      proofFilesList.value.forEach((data, index) => {
        if (!!data.file && data.file instanceof File) {
          data.uploadProgress = 0;
          const uploadPromise = new Promise<void>(resolve => {
            if (!!data.file && data.file instanceof File) {
              uploadData.push({
                file: data.file,
                fileIdUpload: { ...uploadUrls[index], fileId: fileIds[index] },
                onUploadProgress: p => {
                  set(proofFilesList.value[index], "uploadProgress", calculateProgress(p));
                },
                onSuccess: async uuid => {
                  await updateFile({
                    data: {
                      fileId: fileIds[index],
                      uuid: uuid,
                    },
                  });
                  set(proofFilesList.value[index], "uploadProgress", 100);
                  resolve();
                },
              });
            }
          });
          promises.push(uploadPromise);
        }
      });

      uploadFilesToS3(uploadData);
      await Promise.all(promises);
    };

    const sendReport = async () => {
      if (!reportSubject.value || !reportedUserId.value || !selectedReasonIds.value.length || !isValid.value) return;

      try {
        processing.value = true;
        const data: InputUserAbuseReport = {
          subject: reportSubject.value,
          reportedUserId: reportedUserId.value,
          reporterId: authUser.value?.id,
          reporterEmail: guestInfoValues.value.email,
          reporterName: guestInfoValues.value.name,
          reasonIds: selectedReasonIds.value,
          comment: comment.value,
        };
        const response = await providers.ReportsProvider.userReportCreate({ data });

        if (authUser.value && proofFilesList.value.length > 0) {
          await uploadFiles(response);
        }
        onCloseModal();

        emitter.emit<NotificationData>(EVENTS.NOTIFICATION_SHOW, {
          text: $t("Report sent. Thank you for making our community better!"),
          position: "top",
        });
      } finally {
        processing.value = false;
      }
    };

    const onInitiateReport = (data?: AbuseReportData) => {
      reportedUserId.value = data?.reportedUserId || null;
      reportSubject.value = data?.subject || null;
    };

    watch(
      show,
      value => {
        if (value) {
          getReportReasons();
        }
      },
      { immediate: true }
    );

    emitter.on<AbuseReportData>(EVENTS.OPEN_REPORT_MODAL, onInitiateReport);

    onBeforeUnmount(() => {
      emitter.off<AbuseReportData>(EVENTS.OPEN_REPORT_MODAL, onInitiateReport);
    });

    return {
      authUser,
      stepperElRef,
      show,
      selectedReasons,
      loading,
      processing,
      reasons,
      currentStep,
      steps,
      tipItem,
      selectedReasonIds,
      proofFilesList,
      isValid,
      guestInfoValues,
      comment,
      getStepName,
      onCurrentStepChange,
      onCloseModal,
      sendReport,
      onAddFile,
      onRemoveFile,
    };
  },
});
